import { Address } from '@ton/core';
import { useEffect, useRef, useState } from 'react';
import { api, TokenSummary } from '../lib/api';
import { formatTon } from '../lib/format';

export function DiscoveryScreen({ address, session, onOpen, onLaunch }: {
  address: string; session: string | null; onOpen: (curve: string) => void; onLaunch: () => void;
}) {
  const [tab, setTab] = useState<'all' | 'mine'>('all');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<TokenSummary[]>([]);
  const [mine, setMine] = useState<string[]>([]);
  const [offset, setOffset] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [moreBusy, setMoreBusy] = useState(false);
  const generation = useRef(0);
  const owner = address ? Address.parse(address).toRawString() : '';
  const feedQuery = `query=${encodeURIComponent(query)}${tab === 'mine' ? `&mine=1&owner=${encodeURIComponent(owner)}` : ''}`;

  useEffect(() => {
    let cancelled = false;
    setMine([]);
    if (session) void api<{ items: Array<{ curve_address: string | null }> }>('/api/launch-drafts', session)
      .then(({ items }) => { if (!cancelled) setMine(items.flatMap((d) => d.curve_address ? [d.curve_address] : [])); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [session, refresh]);

  useEffect(() => {
    generation.current++;
    const controller = new AbortController();
    setItems([]); setOffset(null); setMoreBusy(false);
    setLoading(true);
    setError(null);
    const timeout = window.setTimeout(() => {
      void api<{ items: TokenSummary[]; nextOffset: number | null }>(`/api/tokens?${feedQuery}`, session, undefined, undefined, controller.signal)
        .then((data) => { setItems(data.items); setOffset(data.nextOffset); })
        .catch((e) => { if (!controller.signal.aborted) setError('Не удалось загрузить токены. Проверьте соединение и повторите.'); })
        .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    }, 250);
    return () => { controller.abort(); window.clearTimeout(timeout); };
  }, [feedQuery, session, refresh]);

  useEffect(() => {
    const timer = window.setInterval(() => { if (!document.hidden) setRefresh((v) => v + 1); }, 30000);
    return () => window.clearInterval(timer);
  }, []);

  const isMine = (item: TokenSummary) => tab === 'mine' || item.deployer === owner || mine.includes(item.curve_address);
  const visible = items;
  async function loadMore() {
    if (offset === null || moreBusy) return;
    const currentGeneration = generation.current;
    setMoreBusy(true);
    try {
      const page = await api<{ items: TokenSummary[]; nextOffset: number | null }>(`/api/tokens?${feedQuery}&offset=${offset}`, session);
      if (currentGeneration !== generation.current) return;
      setItems((prev) => [...prev, ...page.items.filter((item) => !prev.some((p) => p.curve_address === item.curve_address))]);
      setOffset(page.nextOffset);
    } catch { if (currentGeneration === generation.current) setError('Не удалось загрузить следующую страницу. Попробуйте ещё раз.'); }
    finally { if (currentGeneration === generation.current) setMoreBusy(false); }
  }

  return <main className="scroll discovery">
    <section className="discovery-heading"><div><span className="eyebrow">GONG · TON TESTNET</span><h2>Токены сообществ</h2>
      <p>Находите новые запуски и поддерживайте своё сообщество.</p></div></section>
    <button className="launch-banner" onClick={onLaunch}><span className="launch-emblem" aria-hidden="true">↗</span>
      <span><strong>Ваш канал. Ваш токен.</strong><small>Создайте токен и соберите сообщество</small></span><span aria-hidden="true">→</span></button>
    <label className="search-field"><span aria-hidden="true">⌕</span><input aria-label="Поиск токенов" placeholder="Название, тикер или адрес" value={query} onChange={(e) => setQuery(e.target.value)} /></label>
    <div className="feed-toolbar"><div className="feed-tabs" role="tablist" aria-label="Токены">
      <button role="tab" aria-selected={tab === 'all'} onClick={() => setTab('all')}>Все токены</button>
      <button role="tab" aria-selected={tab === 'mine'} onClick={() => setTab('mine')}>Мои запуски</button>
    </div><button className="refresh-button" aria-label="Обновить токены" onClick={() => setRefresh((v) => v + 1)}>↻</button></div>
    {error ? <div role="alert" className="banner warn">{error}</div> : null}
    {loading && !items.length ? <div className="token-list" aria-label="Загрузка токенов" aria-busy="true">{[0, 1, 2].map((n) => <div className="token-skeleton" key={n} />)}</div> : null}
    {!loading && !visible.length ? <section className="empty-state"><div aria-hidden="true">◎</div><h3>{tab === 'mine' ? 'Здесь будут ваши токены' : 'Токены не найдены'}</h3>
      <p>{tab === 'mine' ? 'Подключите кошелёк или откройте приложение в Telegram, чтобы увидеть свои запуски.' : 'Попробуйте другой запрос или запустите первый токен.'}</p>
      <button className="btn" onClick={onLaunch}>Перейти в лаунчпад</button></section> : null}
    <div className="token-list">{visible.map((item) => <button className="token-row" key={item.curve_address} onClick={() => onOpen(item.curve_address)}>
      <TokenAvatar name={item.ticker ?? 'T'} image={item.imageUrl} />
      <span className="token-row-info"><strong>{item.name ?? 'Токен'}{isMine(item) ? <i>Ваш</i> : null}</strong><small>${item.ticker ?? item.curve_address.slice(-6)} · {phaseLabel(item.phase)}</small></span>
      <span className="token-row-value"><strong>{formatTon(BigInt(item.real_ton))}</strong><small>в резерве</small></span><span className="row-arrow" aria-hidden="true">›</span>
    </button>)}</div>
    {offset !== null ? <button className="btn secondary" disabled={moreBusy} onClick={() => void loadMore()}>{moreBusy ? 'Загружаем…' : 'Показать ещё'}</button> : null}
    <p className="feed-note">Тестовая сеть TON · используются тестовые монеты</p>
  </main>;
}

export function TokenAvatar({ name, image }: { name: string; image?: string | null }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [image]);
  return <span className="token-avatar">{image && !failed ? <img src={image} alt="" onError={() => setFailed(true)} /> : name.slice(0, 2).toUpperCase()}</span>;
}

function phaseLabel(phase: string) { return ({ Active: 'Торгуется', Initializing: 'Запускается', Graduating: 'Переход на DEX', Graduated: 'На DEX' } as Record<string, string>)[phase] ?? phase; }
